import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useSEO from '../hooks/useSEO';

const { FiShield, FiTruck, FiClock, FiUsers, FiCheckCircle, FiPhone, FiAlertTriangle, FiActivity, FiRefreshCw, FiCpu, FiBell, FiClipboard, FiFileText } = FiIcons;

const Services = () => {
  // Apply Dynamic SEO
  useSEO({
    title: 'Mobile Diesel Truck Services | CARB Clean Truck Check Ontario CA',
    description: 'Ramirez Mobile Clean Truck offers HD I/M Clean Truck Checks, DOT Annuals, 90-Day BIT Inspections, computer diagnostics, and force regens. Mobile service in Southern California.',
    canonicalRoute: '/services'
  });

  // Inject Service Schema for Google Rich Snippets
  useEffect(() => {
    const scriptId = 'services-schema-json-ld';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "CARB Clean Truck Check and Diesel Emissions Testing",
        "provider": {
          "@type": "AutoRepair",
          "name": "Ramirez Mobile Clean Truck",
          "image": "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/f2db29be14ca2eb40f068da8d183a853.jpeg"
        },
        "areaServed": {
          "@type": "State",
          "name": "California"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Mobile Emissions Testing Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "HD I/M Clean Truck Check",
                "description": "Official CARB compliance testing for diesel fleets."
              },
              "price": "150.00",
              "priceCurrency": "USD"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Opacity Testing",
                "description": "SAE J1667 Snap-Acceleration smoke opacity testing."
              },
              "price": "250.00",
              "priceCurrency": "USD"
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Computer Diagnostics & Force Regens",
                "description": "Advanced scanners to read engine codes and clear DPF systems."
              },
              "price": "120.00",
              "priceCurrency": "USD"
            }
          ]
        }
      });
      document.head.appendChild(script);
    }
    return () => {
      const script = document.getElementById(scriptId);
      if (script) document.head.removeChild(script);
    };
  }, []);

  const mainServices = [
    {
      icon: FiShield,
      title: "Clean Truck Check (HD I/M)",
      description: "Official CARB Clean Truck Checks and compliance testing to ensure your vehicle meets all California state regulations. Ramirez Mobile Clean Truck is fully certified.",
      features: [
        "Official CARB testing",
        "Compliance documentation",
        "Violation resolution assistance",
        "Fleet compliance management"
      ],
      price: "Starting at $150"
    },
    {
      icon: FiTruck,
      title: "90-Day BIT Inspection",
      description: "Mobile 90-Day BIT (Biennial Inspection of Terminals) inspections to keep your fleet safe and compliant with CHP regulations.",
      features: [
        "Brake system inspection",
        "Steering & suspension check",
        "Tire & wheel safety check",
        "CHP compliance documentation"
      ],
      price: "Call for pricing"
    },
    {
      icon: FiClipboard,
      title: "DOT Annual Inspection",
      description: "Complete mobile DOT Annual Inspections required by the FMCSA for all commercial motor vehicles.",
      features: [
        "Federal regulation compliance",
        "Annual inspection sticker",
        "Comprehensive safety audit",
        "On-site certification"
      ],
      price: "Starting at $120"
    },
    {
      icon: FiRefreshCw,
      title: "DPF Force Regens",
      description: "Professional forced regeneration services to clear soot and ash from your Diesel Particulate Filter (DPF) delivered directly to your location.",
      features: [
        "On-site forced regeneration",
        "Soot load reduction",
        "Performance restoration",
        "System verification"
      ],
      price: "Call for pricing"
    },
    {
      icon: FiCpu,
      title: "Computer Diagnostics Services",
      description: "Comprehensive computer diagnostics to identify engine codes, emissions faults, and DEF/SCR system irregularities.",
      features: [
        "Check engine light analysis",
        "Emissions system scan",
        "Fault code interpretation",
        "Repair recommendations"
      ],
      price: "Starting at $120"
    },
    {
      icon: FiActivity,
      title: "Smoke Opacity Testing",
      description: "Professional SAE J1667 smoke opacity testing to measure exhaust emissions and ensure regulatory standards.",
      features: [
        "SAE J1667 Snap-Acceleration test",
        "Smoke meter analysis",
        "Performance verification",
        "Official certification"
      ],
      price: "Starting at $250"
    }
  ];

  const additionalServices = [
    "Fleet Management",
    "Emergency Service",
    "Aftertreatment System Repair",
    "SCR System Service",
    "DEF System Maintenance",
    "Preventive Maintenance"
  ];

  return (
    <div className="bg-gray-900 text-white">
      {/* Hero Section */}
      <section className="relative bg-gray-900 py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/f5e298b7d2339818def7142c2f1a2bf6.jpeg" 
            alt="Truck Repair Shop Services" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-800/90"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Ramirez Mobile Clean Truck: CARB Compliance & Safety Services
            </motion.h1>
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl mb-8 text-gray-300"
            >
              Clean Truck Checks, BIT & DOT Inspections in Ontario, CA
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-400"
            >
              Comprehensive mobile compliance solutions for diesel trucks throughout Southern California. We bring the shop to you.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Reminder Promo */}
      <section className="bg-yellow-500 py-4">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-center text-center md:text-left gap-4">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiBell} className="text-2xl text-gray-900" aria-hidden="true" />
            <span className="font-bold text-gray-900 text-lg">Don't Miss a Deadline!</span>
          </div>
          <p className="text-gray-900">Get free automated alerts from Ramirez Mobile Clean Truck for your upcoming inspections.</p>
          <Link 
            to="/reminders" 
            className="bg-gray-900 text-white px-6 py-2 rounded-full font-bold hover:bg-gray-800 transition-colors text-sm"
            aria-label="Sign up for inspection reminders"
          >
            Sign Up Now
          </Link>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Complete Mobile Compliance Solutions</h2>
            <h3 className="text-2xl text-gray-400">Expert Services for California Fleets</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-800 rounded-lg p-8 hover:shadow-lg transition-shadow border-l-4 border-blue-500 flex flex-col h-full"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <SafeIcon icon={service.icon} className="text-4xl text-blue-500" aria-hidden="true" />
                  <div>
                    <h4 className="text-2xl font-bold text-white">{service.title}</h4>
                    <p className="text-lg text-blue-400 font-semibold">{service.price}</p>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6 flex-grow">{service.description}</p>
                
                <div className="space-y-3 mb-8">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-auto">
                  <a 
                    href="tel:9092842738" 
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors inline-flex justify-center items-center space-x-2"
                    aria-label={`Call to book ${service.title}`}
                  >
                    <SafeIcon icon={FiPhone} aria-hidden="true" />
                    <span>Call (909) 284-2738</span>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Service Process</h2>
            <h3 className="text-2xl text-gray-400">Simple, Professional, Reliable</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Contact Us", desc: "Call Ramirez Mobile or request service online" },
              { step: "2", title: "Schedule", desc: "We arrange convenient service time at your yard" },
              { step: "3", title: "Service", desc: "Professional on-site testing and diagnostics" },
              { step: "4", title: "Compliance", desc: "Complete documentation provided for CARB/DMV" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center bg-gray-700 p-6 rounded-lg shadow-sm border border-gray-600"
              >
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-md">
                  {item.step}
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">{item.title}</h4>
                <p className="text-gray-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="lg:w-1/2">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/5b12c1751e7656c8707101b12103a2dc.jpg" 
                alt="Truck Engine Detail Aftertreatment System" 
                className="rounded-lg shadow-xl w-full h-auto object-cover border border-gray-800"
                loading="lazy"
              />
            </div>
            <div className="lg:w-1/2">
              <div className="mb-10">
                <h2 className="text-4xl font-bold text-white mb-4">Additional Maintenance Services</h2>
                <h3 className="text-2xl text-gray-400">Comprehensive Diesel Truck Support</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {additionalServices.map((service, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-gray-800 p-4 rounded-lg flex items-center space-x-3 hover:bg-gray-700 transition-colors border border-gray-700"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-xl text-blue-500" aria-hidden="true" />
                    <h4 className="font-semibold text-gray-200">{service}</h4>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Service Alert */}
      <section className="py-16 bg-red-900/20 border-l-4 border-red-600">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-4 mb-8">
            <SafeIcon icon={FiAlertTriangle} className="text-3xl text-red-500" aria-hidden="true" />
            <h2 className="text-3xl font-bold text-red-500">Emergency Service Available</h2>
          </div>
          
          <div className="text-center max-w-3xl mx-auto">
            <h3 className="text-xl text-white mb-4">
              Facing a CARB compliance deadline or emergency situation?
            </h3>
            <p className="text-gray-300 mb-8">
              Ramirez Mobile Clean Truck provides emergency CARB compliance and inspection services throughout Ontario, CA and surrounding areas. Don't risk fines or having your trucks sidelined - call us now for immediate assistance.
            </p>
            <a 
              href="tel:9092842738" 
              className="bg-red-600 text-white font-bold px-8 py-4 rounded-lg hover:bg-red-700 transition-colors text-lg inline-flex items-center space-x-2 shadow-lg"
              aria-label="Call emergency service line"
            >
              <SafeIcon icon={FiPhone} aria-hidden="true" />
              <span>Emergency Line: (909) 284-2738</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Compliant?</h2>
          <h3 className="text-2xl text-gray-300 mb-8">Professional CARB Compliance Services in Ontario, CA</h3>
          <p className="text-xl mb-8 text-gray-400 max-w-3xl mx-auto">
            Don't wait until it's too late. Ensure your diesel trucks meet California's strict emissions standards with Ramirez Mobile Clean Truck's professional mobile CARB compliance services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:9092842738" 
              className="bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg"
              aria-label="Call to book service"
            >
              Call (909) 284-2738
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-colors text-lg"
              aria-label="Request a service quote"
            >
              Request Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;